import { useEffect, useMemo } from "react";
import { useKeycloakContext } from "@context/KeycloakProvider";
import { appInsights } from "@telemetry/appInsights";
import { startLoginRedirect } from "@telemetry/trackLogin";
import { paths } from "@utils/navigation";
import { Navigate, useLocation } from "react-router-dom";
import AppRoutes from "./routes";
import "./App.scss";
import { completeLoginSubmitToSessionReady } from "@telemetry/loginSubmitTiming";
import { APP_KPI_NAME } from "@telemetry/utils";

const normalizePath = (p: string) => p.replace(/\/+$/, "");

function trackEventSafely(event: Parameters<typeof appInsights.trackEvent>[0]) {
  try {
    appInsights.trackEvent(event);
  } catch {
    // Telemetry must never prevent authentication or route rendering.
  }
}

function App() {
  const { keycloak, initialized } = useKeycloakContext();
  const location = useLocation();
  const isLoginRoute = normalizePath(location.pathname) === "/login";
  const isPublicRoute = useMemo(() => {
    const current = normalizePath(location.pathname);

    const publicPaths = [
      normalizePath(paths.vendorLicenseRequest),
      normalizePath(paths.vendorLicenseRequestConfirmation),
      normalizePath(paths.checkSecurityClearance),
      "/logged-out",
    ];

    return publicPaths.includes(current);
  }, [location.pathname]);

  useEffect(() => {
    trackEventSafely({
      name: APP_KPI_NAME.PCS_APP_ROUTE_CONTEXT,
      properties: {
        path: location.pathname,
        isPublicRoute,
        initialized,
        authenticated: !!keycloak?.authenticated,
      },
    });
  }, [location.pathname, isPublicRoute, initialized, keycloak?.authenticated]);

  useEffect(() => {
    if (!isPublicRoute && initialized && !keycloak?.authenticated) {
      trackEventSafely({
        name: APP_KPI_NAME.PCS_AUTH_LOGIN_REDIRECT_STARTED,
        properties: {
          path: location.pathname,
        },
      });

      if (keycloak) {
        globalThis.performance?.mark?.("keycloak-login-called");
        startLoginRedirect(
          {
            path: location.pathname,
            trigger: "protected_route_redirect",
          },
          () =>
            keycloak.login(
              isLoginRoute ? { redirectUri: `${window.location.origin}/` } : undefined,
            ),
        );
      }
    }
  }, [keycloak, initialized, isLoginRoute, isPublicRoute, location.pathname]);

  useEffect(() => {
    if (!isPublicRoute && initialized && keycloak?.authenticated) {
      trackEventSafely({
        name: APP_KPI_NAME.PCS_AUTH_SESSION_READY,
        properties: {
          path: location.pathname,
        },
      });
      completeLoginSubmitToSessionReady({ path: location.pathname });
    }
  }, [initialized, isPublicRoute, keycloak?.authenticated, location.pathname]);

  if (!initialized && !isPublicRoute) return null;
  if (!isPublicRoute && !keycloak?.authenticated) return null;
  if (isLoginRoute) return <Navigate to={paths.index} replace />;

  return <AppRoutes />;
}

export default App;
